// src/domains/broadcast.actions.ts —— 推广推送数据与动作：全部经 broadcast-contract 的 bcCall / bcBlob（唯一契约替换点）；
// 每个异步结果写回前比对会话代（跨账号不串数据）；run 详情 5s 被动轮询（X-Activity: passive，不刷新空闲计时）
import { ApiError } from '../../../shared/src/api/client';
import { interpolate } from '../../../shared/src/errors';
import { createPoller, type Poller } from '../../../shared/src/poll';
import { todayIn } from '../../../shared/src/time';
import {
  bcBlob,
  bcCall,
  pageItems,
  type BcBotOption,
  type BcCampaign,
  type BcDelivery,
  type BcEstimate,
  type BcFile,
  type BcPage,
  type BcQuota,
  type BcRun,
  type BcStatus,
} from '../broadcast-contract';
import type { AgentGp } from '../gp';
import { IMG_MAX_BYTES, IMG_TYPES } from './broadcast.lib';
import {
  DL_SIZE,
  LIST_SIZE,
  RUNS_SIZE,
  bcOf,
  draftOf,
  emptyDraft,
  freshBc,
  inputOf,
  platformTz,
  runActive,
  setBc,
  setDraft,
  validateDraft,
  type BcDraft,
  type DlTab,
} from './broadcast.store';
import { str, type AgentCtx } from './types';

export const POLL_MS = 5000;

// ---- 资源：object URL 与轮询器（按应用实例隔离，不跨挂载泄漏） ----

function revoke(url: string): void {
  if (url && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
}

function objectUrl(b: Blob): string {
  return typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(b) : '';
}

const pollers = new WeakMap<AgentGp, Poller>();

export function stopPoll(ctx: AgentCtx): void {
  pollers.get(ctx.gp)?.stop();
  pollers.delete(ctx.gp);
}

// 宿主卸载：停轮询、释放缩略图（不再 setState）
export function releaseBc(ctx: AgentCtx): void {
  stopPoll(ctx);
  for (const u of Object.values(bcOf(ctx.host.state).thumbs)) revoke(u);
}

// 离开页面 / 会话结束：释放资源后切片复位（未保存草稿一律丢弃）
export function resetBc(ctx: AgentCtx): void {
  releaseBc(ctx);
  ctx.host.setState({ bc: freshBc() });
}

// ---- 错误映射（只复用现有 apperr：1503 FeatureOff / 1002 RateLimited / 1012 Precondition reason / 4040 / 4220） ----

const FIELD_LABEL: Readonly<Record<string, string>> = {
  title: 'bcFTitle',
  bot_ids: 'bcFBots',
  filter_card: 'bcFCard',
  filter_lang: 'bcFLang',
  text_zh: 'bcFTextZh',
  text_en: 'bcFTextEn',
  image_file_id: 'bcFImage',
  file: 'bcFImage',
  buttons: 'bcFButtons',
  schedule: 'bcFSchedule',
  run_at: 'bcFRunAt',
  time_of_day: 'bcFTime',
  weekdays: 'bcFWeekdays',
  end_date: 'bcFEndDate',
};

export type ErrEffect = 'none' | 'reload' | 'off';

export function bcErr(ctx: AgentCtx, e: unknown): { text: string; effect: ErrEffect } {
  const t = ctx.t;
  if (e instanceof ApiError) {
    if (e.code === 1503) return { text: str(t, 'bcErrFeatureOff'), effect: 'off' };
    if (e.code === 1002) return { text: str(t, 'bcErrQuota'), effect: 'none' };
    if (e.code === 4040) return { text: str(t, 'bcErrNotFound'), effect: 'none' };
    if (e.code === 1012) {
      const r = e.data.reason;
      if (r === 'stale_version') return { text: str(t, 'bcErrStale'), effect: 'reload' };
      if (r === 'campaign_status') return { text: str(t, 'bcErrStatus'), effect: 'reload' };
      if (r === 'no_telegram_binding') return { text: str(t, 'bcErrNoTg'), effect: 'none' };
      if (r === 'preview_blocked') return { text: str(t, 'bcErrPreviewBlocked'), effect: 'none' };
    }
    if (e.code === 4220) {
      if (e.data.reason === 'text_too_long') return { text: str(t, 'bcErrTooLongSrv'), effect: 'none' };
      const f = e.data.field ?? '';
      if (f) {
        const key = FIELD_LABEL[f] ?? FIELD_LABEL[f.split(/[.[]/)[0] ?? ''];
        return { text: interpolate(str(t, 'bcErrField'), { field: key ? str(t, key) : f }), effect: 'none' };
      }
    }
  }
  return { text: ctx.gp.ui.err(e), effect: 'none' };
}

// 写操作失败的统一后续：FeatureOff → 重拉配额（整页切「未开放」）；stale / 状态变化 → 重拉活动并回详情
async function afterErr(ctx: AgentCtx, e: unknown, campaignId: number | null, inForm: boolean): Promise<void> {
  const { text, effect } = bcErr(ctx, e);
  if (effect === 'off') {
    ctx.gp.ui.toast(text);
    await loadQuota(ctx);
    return;
  }
  if (effect === 'reload' && campaignId !== null) {
    ctx.gp.ui.toast(text);
    await openDetail(ctx, campaignId);
    return;
  }
  if (inForm) setBc(ctx, { formErr: text });
  else ctx.gp.ui.toast(text);
}

// ---- 读取 ----

export async function loadQuota(ctx: AgentCtx): Promise<boolean> {
  const ep = ctx.gp.session.epoch();
  try {
    const quota = await bcCall<BcQuota>(ctx.gp.api, 'GET /api/agent/broadcast-quota');
    if (ctx.gp.session.epoch() !== ep) return false;
    setBc(ctx, { quota, loadErr: '' });
    return quota.enabled;
  } catch (e) {
    if (ctx.gp.session.epoch() === ep) setBc(ctx, { loadErr: bcErr(ctx, e).text });
    console.warn('[gp:agent:bc] 配额加载失败', e);
    return false;
  }
}

async function loadBots(ctx: AgentCtx): Promise<void> {
  const ep = ctx.gp.session.epoch();
  try {
    const bots = await bcCall<BcBotOption[]>(ctx.gp.api, 'GET /api/agent/broadcast-bots');
    if (ctx.gp.session.epoch() === ep) setBc(ctx, { bots: Array.isArray(bots) ? bots : [] });
  } catch (e) {
    if (ctx.gp.session.epoch() === ep) ctx.gp.ui.toast(bcErr(ctx, e).text);
  }
}

export async function loadList(ctx: AgentCtx, page: number, status: '' | BcStatus): Promise<void> {
  const ep = ctx.gp.session.epoch();
  try {
    const r = await bcCall<BcPage<BcCampaign>>(ctx.gp.api, 'GET /api/agent/broadcasts', { query: { status, page, size: LIST_SIZE } });
    if (ctx.gp.session.epoch() !== ep) return;
    setBc(ctx, { list: pageItems(r), total: r.total, page, status });
  } catch (e) {
    if (ctx.gp.session.epoch() === ep) ctx.gp.ui.toast(bcErr(ctx, e).text);
  }
}

// 进入本页：先拉配额（决定整页开关），开放时再并发拉 Bot 与列表
export async function enter(ctx: AgentCtx): Promise<void> {
  resetBc(ctx);
  if (!(await loadQuota(ctx))) return;
  await Promise.all([loadBots(ctx), loadList(ctx, 1, '')]);
}

async function loadThumb(ctx: AgentCtx, id: number | null): Promise<void> {
  if (id === null || bcOf(ctx.host.state).thumbs[id] !== undefined) return;
  const ep = ctx.gp.session.epoch();
  try {
    const { blob } = await bcBlob(ctx.gp.api, 'GET /api/agent/files/broadcast/{id}', { path: { id } });
    const url = objectUrl(blob);
    if (ctx.gp.session.epoch() !== ep || ctx.host.state.page !== 'broadcast') {
      revoke(url);
      return;
    }
    setBc(ctx, { thumbs: { ...bcOf(ctx.host.state).thumbs, [id]: url } });
  } catch (e) {
    console.warn('[gp:agent:bc] 图片读取失败', id, e);
  }
}

export async function loadRuns(ctx: AgentCtx, id: number, page: number): Promise<void> {
  const ep = ctx.gp.session.epoch();
  try {
    const r = await bcCall<BcPage<BcRun>>(ctx.gp.api, 'GET /api/agent/broadcasts/{id}/runs', { path: { id }, query: { page, size: RUNS_SIZE } });
    if (ctx.gp.session.epoch() !== ep) return;
    setBc(ctx, { runs: pageItems(r), runsTotal: r.total, runsPage: page });
  } catch (e) {
    if (ctx.gp.session.epoch() === ep) ctx.gp.ui.toast(bcErr(ctx, e).text);
  }
}

export async function openDetail(ctx: AgentCtx, id: number): Promise<void> {
  stopPoll(ctx);
  const ep = ctx.gp.session.epoch();
  try {
    const cur = await bcCall<BcCampaign>(ctx.gp.api, 'GET /api/agent/broadcasts/{id}', { path: { id } });
    if (ctx.gp.session.epoch() !== ep) return;
    setBc(ctx, { view: 'detail', cur, draft: null, estimate: null, formErr: '', confirm: null, run: null, runs: [], runsTotal: 0, runsPage: 1 });
    await Promise.all([loadRuns(ctx, id, 1), loadThumb(ctx, cur.image_file_id)]);
  } catch (e) {
    if (ctx.gp.session.epoch() === ep) ctx.gp.ui.toast(bcErr(ctx, e).text);
  }
}

export async function loadDeliveries(ctx: AgentCtx, rid: number, tab: DlTab, page: number, passive = false): Promise<void> {
  const ep = ctx.gp.session.epoch();
  const query = tab === 'skipped' ? { status: 'skipped' } : tab === 'failed' ? { status: 'failed' } : { status: 'failed', fail_kind: tab };
  try {
    const r = await bcCall<BcPage<BcDelivery>>(ctx.gp.api, 'GET /api/agent/broadcast-runs/{rid}/deliveries', { path: { rid }, query: { ...query, page, size: DL_SIZE }, passive });
    if (ctx.gp.session.epoch() !== ep) return;
    setBc(ctx, { dl: pageItems(r), dlTotal: r.total, dlPage: page, dlTab: tab });
  } catch (e) {
    if (ctx.gp.session.epoch() === ep && !passive) ctx.gp.ui.toast(bcErr(ctx, e).text);
    if (passive) throw e;
  }
}

async function refreshRun(ctx: AgentCtx, rid: number, passive: boolean): Promise<BcRun | null> {
  const ep = ctx.gp.session.epoch();
  const run = await bcCall<BcRun>(ctx.gp.api, 'GET /api/agent/broadcast-runs/{rid}', { path: { rid }, passive });
  if (ctx.gp.session.epoch() !== ep) return null;
  setBc(ctx, { run });
  return run;
}

// run 详情：立即拉取；进行中（queued / snapshotting / sending / paused）每 5s 刷新进度与当前明细页，结束即停
export async function openRun(ctx: AgentCtx, rid: number): Promise<void> {
  stopPoll(ctx);
  setBc(ctx, { view: 'run', run: null, dl: [], dlTotal: 0, dlPage: 1, dlTab: 'failed' });
  const ep = ctx.gp.session.epoch();
  try {
    const run = await refreshRun(ctx, rid, false);
    if (!run) return;
    await loadDeliveries(ctx, rid, 'failed', 1);
    if (runActive(run.status) && ctx.gp.session.epoch() === ep && bcOf(ctx.host.state).view === 'run') startPoll(ctx, rid);
  } catch (e) {
    if (ctx.gp.session.epoch() === ep) ctx.gp.ui.toast(bcErr(ctx, e).text);
  }
}

function startPoll(ctx: AgentCtx, rid: number): void {
  const ep = ctx.gp.session.epoch();
  // 首轮已由 openRun 拉取：跳过 createPoller 启动时的立即执行（schedule(0)），从第一个间隔开始刷新
  let first = true;
  const p = createPoller(async () => {
    if (first) {
      first = false;
      return;
    }
    const st = bcOf(ctx.host.state);
    if (ctx.gp.session.epoch() !== ep || ctx.host.state.page !== 'broadcast' || st.view !== 'run' || st.run?.id !== rid) {
      stopPoll(ctx);
      return;
    }
    const run = await refreshRun(ctx, rid, true);
    if (!run) return;
    const now = bcOf(ctx.host.state);
    await loadDeliveries(ctx, rid, now.dlTab, now.dlPage, true);
    if (!runActive(run.status)) stopPoll(ctx);
  }, POLL_MS);
  pollers.set(ctx.gp, p);
  p.start();
}

// ---- 编辑器 ----

export function newDraft(ctx: AgentCtx): void {
  if (!bcOf(ctx.host.state).quota?.enabled) return;
  stopPoll(ctx);
  setBc(ctx, { view: 'edit', draft: emptyDraft(ctx.host.state), estimate: null, formErr: '', confirm: null });
}

export function editCurrent(ctx: AgentCtx): void {
  const cur = bcOf(ctx.host.state).cur;
  if (!cur) return;
  setBc(ctx, { view: 'edit', draft: draftOf(cur, platformTz(ctx.host.state)), estimate: null, formErr: '', confirm: null });
  void loadThumb(ctx, cur.image_file_id);
}

interface FileEvent {
  target: { files?: ArrayLike<File> | null; value?: string };
}

export async function pickImage(ctx: AgentCtx, e: FileEvent): Promise<void> {
  const f = e.target.files?.[0];
  if (!f) return;
  try {
    e.target.value = ''; // 允许重复选择同一文件
  } catch (err) {
    console.warn('[gp:agent:bc] 复位文件输入失败', err);
  }
  const t = ctx.t;
  if (!IMG_TYPES.includes(f.type)) return setBc(ctx, { formErr: str(t, 'bcErrImgType') });
  if (f.size > IMG_MAX_BYTES) return setBc(ctx, { formErr: str(t, 'bcErrImgSize') });
  if (bcOf(ctx.host.state).uploading) return;
  const ep = ctx.gp.session.epoch();
  setBc(ctx, { uploading: true, formErr: '' });
  try {
    const form = new FormData();
    form.append('file', f, f.name);
    const r = await bcCall<BcFile>(ctx.gp.api, 'POST /api/agent/broadcast-files', { form });
    if (ctx.gp.session.epoch() !== ep) return;
    const url = objectUrl(f);
    const st = bcOf(ctx.host.state);
    setBc(ctx, { uploading: false, thumbs: { ...st.thumbs, [r.id]: url } });
    setDraft(ctx, { imageId: r.id }, { keepEstimate: true });
  } catch (err) {
    if (ctx.gp.session.epoch() !== ep) return;
    setBc(ctx, { uploading: false });
    await afterErr(ctx, err, null, true);
  }
}

export async function estimate(ctx: AgentCtx): Promise<void> {
  const d = bcOf(ctx.host.state).draft;
  if (!d) return;
  if (d.botIds.length === 0) return setBc(ctx, { formErr: str(ctx.t, 'bcErrBots') });
  const ep = ctx.gp.session.epoch();
  await ctx.gp.ui.run(async () => {
    try {
      const r = await bcCall<BcEstimate>(ctx.gp.api, 'POST /api/agent/broadcasts/estimate', { body: { bot_ids: d.botIds, filter_card: d.filterCard, filter_lang: d.filterLang } });
      if (ctx.gp.session.epoch() === ep) setBc(ctx, { estimate: r });
    } catch (e) {
      if (ctx.gp.session.epoch() === ep) await afterErr(ctx, e, null, true);
    }
  });
}

// 保存（新建 POST / 编辑 PUT 带 version）；成功返回最新活动，失败已处理并返回 null。调用方负责 ui.run 包裹
async function persist(ctx: AgentCtx, d: BcDraft): Promise<BcCampaign | null> {
  const err = validateDraft(ctx.t, d);
  if (err) {
    setBc(ctx, { formErr: err });
    return null;
  }
  const ep = ctx.gp.session.epoch();
  try {
    const body = inputOf(d);
    const c =
      d.id === null
        ? await bcCall<BcCampaign>(ctx.gp.api, 'POST /api/agent/broadcasts', { body })
        : await bcCall<BcCampaign>(ctx.gp.api, 'PUT /api/agent/broadcasts/{id}', { path: { id: d.id }, body: { ...body, version: d.version } });
    if (ctx.gp.session.epoch() !== ep) return null;
    const cur = bcOf(ctx.host.state).draft;
    // 保存期间用户若继续输入，保留其输入，只回填 id / version / 状态
    setBc(ctx, { cur: c, formErr: '', draft: cur ? { ...cur, id: c.id, version: c.version, status: c.status, dirty: cur !== d } : null });
    return c;
  } catch (e) {
    if (ctx.gp.session.epoch() === ep) await afterErr(ctx, e, d.id, true);
    return null;
  }
}

export async function saveDraft(ctx: AgentCtx): Promise<void> {
  const d = bcOf(ctx.host.state).draft;
  if (!d) return;
  await ctx.gp.ui.run(async () => {
    if (await persist(ctx, d)) ctx.gp.ui.toast(str(ctx.t, 'bcToastSaved'));
  });
}

// 预览须针对已保存内容：未保存 / 有改动先保存再发
export async function sendPreview(ctx: AgentCtx): Promise<void> {
  const d = bcOf(ctx.host.state).draft;
  if (!d) return;
  await ctx.gp.ui.run(async () => {
    const c = d.id === null || d.dirty ? await persist(ctx, d) : bcOf(ctx.host.state).cur;
    const id = c?.id ?? d.id;
    if (id === null) return;
    const ep = ctx.gp.session.epoch();
    try {
      await bcCall(ctx.gp.api, 'POST /api/agent/broadcasts/{id}/preview', { path: { id } });
      if (ctx.gp.session.epoch() === ep) ctx.gp.ui.toast(str(ctx.t, 'bcToastPreview'));
    } catch (e) {
      if (ctx.gp.session.epoch() === ep) await afterErr(ctx, e, null, true);
    }
  });
}

// ---- 提交前配额提示（不拦截，服务端为准） ----

export function submitWarnings(ctx: AgentCtx, c: BcCampaign): string[] {
  const st = bcOf(ctx.host.state);
  const q = st.quota;
  if (!q) return [];
  const t = ctx.t;
  const out: string[] = [];
  const sc = c.schedule;
  const tz = c.timezone || platformTz(ctx.host.state);
  const runsToday = sc.kind === 'now' || (sc.kind === 'once' && !!sc.run_at && todayIn(tz, new Date(sc.run_at)) === todayIn(tz));
  if (runsToday) {
    const used = new Map(q.used_today.map((u) => [u.bot_id, u.used] as const));
    const full = c.bots.filter((b) => (used.get(b.id) ?? 0) >= q.daily_per_bot).map((b) => `@${b.username}`);
    if (full.length > 0) out.push(interpolate(str(t, 'bcWarnDaily'), { bots: full.join(' ') }));
  }
  if ((sc.kind === 'daily' || sc.kind === 'weekly') && q.periodic_active >= q.max_periodic) out.push(interpolate(str(t, 'bcWarnPeriodic'), { max: q.max_periodic }));
  const est = st.estimate;
  if (est && est.total > q.max_targets) out.push(interpolate(str(t, 'bcWarnTargets'), { n: est.total, max: q.max_targets }));
  return out;
}

async function doSubmit(ctx: AgentCtx, c: BcCampaign): Promise<void> {
  const ep = ctx.gp.session.epoch();
  try {
    const r = await bcCall<BcCampaign>(ctx.gp.api, 'POST /api/agent/broadcasts/{id}/submit', { path: { id: c.id }, body: { version: c.version } });
    if (ctx.gp.session.epoch() !== ep) return;
    ctx.gp.ui.toast(str(ctx.t, r.status === 'pending_review' ? 'bcToastSubmittedReview' : 'bcToastSubmitted'));
    setBc(ctx, { confirm: null });
    await Promise.all([openDetail(ctx, r.id), loadQuota(ctx)]);
  } catch (e) {
    if (ctx.gp.session.epoch() === ep) await afterErr(ctx, e, c.id, bcOf(ctx.host.state).view === 'edit');
  }
}

// 编辑器「提交」：先保存（有改动 / 新建），再按配额提示确认后提交
export async function submitFromEditor(ctx: AgentCtx): Promise<void> {
  const d = bcOf(ctx.host.state).draft;
  if (!d) return;
  await ctx.gp.ui.run(async () => {
    const c = d.id === null || d.dirty ? await persist(ctx, d) : bcOf(ctx.host.state).cur;
    if (!c) return;
    const warn = submitWarnings(ctx, c);
    if (warn.length > 0) return setBc(ctx, { confirm: { kind: 'submit', id: c.id, lines: warn } });
    await doSubmit(ctx, c);
  });
}

export async function submitCurrent(ctx: AgentCtx): Promise<void> {
  const c = bcOf(ctx.host.state).cur;
  if (!c) return;
  const warn = submitWarnings(ctx, c);
  if (warn.length > 0) return setBc(ctx, { confirm: { kind: 'submit', id: c.id, lines: warn } });
  await ctx.gp.ui.run(() => doSubmit(ctx, c));
}

type LifeAction = 'pause' | 'resume' | 'cancel';
const LIFE_ROUTE = {
  pause: 'POST /api/agent/broadcasts/{id}/pause',
  resume: 'POST /api/agent/broadcasts/{id}/resume',
  cancel: 'POST /api/agent/broadcasts/{id}/cancel',
} as const;
const LIFE_TOAST: Readonly<Record<LifeAction, string>> = { pause: 'bcToastPaused', resume: 'bcToastResumed', cancel: 'bcToastCancelled' };

export async function lifecycle(ctx: AgentCtx, action: LifeAction): Promise<void> {
  const c = bcOf(ctx.host.state).cur;
  if (!c) return;
  const ep = ctx.gp.session.epoch();
  await ctx.gp.ui.run(async () => {
    try {
      const r = await bcCall<BcCampaign>(ctx.gp.api, LIFE_ROUTE[action], { path: { id: c.id }, body: { version: c.version } });
      if (ctx.gp.session.epoch() !== ep) return;
      ctx.gp.ui.toast(str(ctx.t, LIFE_TOAST[action]));
      setBc(ctx, { cur: r, confirm: null });
      await Promise.all([loadRuns(ctx, r.id, 1), loadQuota(ctx)]);
    } catch (e) {
      if (ctx.gp.session.epoch() !== ep) return;
      setBc(ctx, { confirm: null });
      await afterErr(ctx, e, c.id, false);
    }
  });
}

export function askCancel(ctx: AgentCtx): void {
  const c = bcOf(ctx.host.state).cur;
  if (c) setBc(ctx, { confirm: { kind: 'cancel', id: c.id, lines: [str(ctx.t, 'bcCfCancelText')] } });
}

export async function confirmGo(ctx: AgentCtx): Promise<void> {
  const st = bcOf(ctx.host.state);
  const cf = st.confirm;
  if (!cf) return;
  if (cf.kind === 'cancel') return lifecycle(ctx, 'cancel');
  const c = st.cur;
  if (!c || c.id !== cf.id) return setBc(ctx, { confirm: null });
  await ctx.gp.ui.run(() => doSubmit(ctx, c));
}

export function backToList(ctx: AgentCtx): void {
  stopPoll(ctx);
  const st = bcOf(ctx.host.state);
  setBc(ctx, { view: 'list', draft: null, cur: null, run: null, estimate: null, formErr: '', confirm: null });
  void loadList(ctx, st.page, st.status);
}

export function backToDetail(ctx: AgentCtx): void {
  const st = bcOf(ctx.host.state);
  const id = st.cur?.id ?? st.run?.campaign_id ?? st.draft?.id ?? null;
  if (id === null) return backToList(ctx);
  void openDetail(ctx, id);
}
